import { useEffect } from 'react'

/**
 * FaqSchema - emits FAQPage JSON-LD for sub-routes that render a visible
 * FAQ, keeping structured data in sync with what the user actually sees.
 *
 * The homepage FAQ is covered by the static FAQPage node in index.html's
 * @graph (present in the initial HTML, so non-JS crawlers get it too), so
 * this overlay deliberately skips the home route to avoid duplicating it.
 * For every other route it reads the active page's `[data-faq]` group and
 * builds a route-scoped FAQPage.
 *
 * Signals it reacts to:
 *   - <html data-route> - the inline router stamps this on every nav.
 *   - `pb-pages-mounted` - sub-page FAQ content injects asynchronously.
 *   - popstate / hashchange - browser navigation + in-page anchors.
 * A few short delayed re-runs cover content injected just after the route
 * flips. The single managed <script id="pb-faq-schema"> is rewritten or
 * removed so only one, always-current FAQPage exists at a time.
 */
const SCRIPT_ID = 'pb-faq-schema'

function currentRouteName() {
  return document.documentElement.getAttribute('data-route') || 'home'
}

function collectFaq(scope) {
  const items = []
  scope.querySelectorAll('[data-faq] > details').forEach((d) => {
    const qEl = d.querySelector('summary .faq-q') || d.querySelector('summary')
    const aEl = d.querySelector('.faq-a')
    if (!qEl || !aEl) return
    const q = (qEl.textContent || '').replace(/\s+/g, ' ').trim()
    const a = (aEl.textContent || '').replace(/\s+/g, ' ').trim()
    if (q && a) items.push({ q, a })
  })
  return items
}

function removeScript() {
  const el = document.getElementById(SCRIPT_ID)
  if (el) el.remove()
}

export default function FaqSchema() {
  useEffect(() => {
    function rebuild() {
      const routeName = currentRouteName()
      // Home FAQ schema ships statically in index.html - don't duplicate.
      if (routeName === 'home') {
        removeScript()
        return
      }
      const active = document.querySelector('.page.active') || document.body
      const items = collectFaq(active)
      if (items.length < 2) {
        removeScript()
        return
      }
      const url = `https://pentabay.com/${routeName}`
      const data = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        url,
        mainEntity: items.map((it) => ({
          '@type': 'Question',
          name: it.q,
          acceptedAnswer: { '@type': 'Answer', text: it.a },
        })),
      }
      let el = document.getElementById(SCRIPT_ID)
      if (!el) {
        el = document.createElement('script')
        el.id = SCRIPT_ID
        el.type = 'application/ld+json'
        document.head.appendChild(el)
      }
      el.textContent = JSON.stringify(data)
    }

    let timers = []
    function rebuildSoon() {
      timers.forEach(clearTimeout)
      rebuild()
      timers = [80, 300, 800].map((ms) => setTimeout(rebuild, ms))
    }
    rebuildSoon()

    const mo = new MutationObserver(rebuildSoon)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-route'] })

    window.addEventListener('pb-pages-mounted', rebuildSoon)
    window.addEventListener('popstate', rebuildSoon)
    window.addEventListener('hashchange', rebuildSoon)

    return () => {
      timers.forEach(clearTimeout)
      mo.disconnect()
      window.removeEventListener('pb-pages-mounted', rebuildSoon)
      window.removeEventListener('popstate', rebuildSoon)
      window.removeEventListener('hashchange', rebuildSoon)
      removeScript()
    }
  }, [])
  return null
}
